import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchUsers, inviteUser, deleteUsers } from '../api/users';
import type { UsersQueryParams, InviteUserRequest } from '../types';

export const usersKeys = {
  all: ['users'] as const,
  lists: () => [...usersKeys.all, 'list'] as const,
  list: (params: UsersQueryParams) =>
    [...usersKeys.lists(), params] as const,
};

// Callers that mount the list behind a closed dialog pass enabled: false to skip the fetch.
export function useUsers(params: UsersQueryParams, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: usersKeys.list(params),
    queryFn: () => fetchUsers(params),
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InviteUserRequest) => inviteUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

export function useDeleteUsers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userIds: string[]) => deleteUsers(userIds),
    onSuccess: () => {
      // Invalidate only user list queries for more precise cache management
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
    },
  });
}
